// SPDX-License-Identifier: GPL-3.0-or-later
//
// Tiny theme module. Applies `data-theme` to <html>, persists choice in
// localStorage, falls back to OS `prefers-color-scheme`.

export type Theme = 'dark' | 'light';
const STORAGE_KEY = 'applify:theme';

type Listener = (t: Theme) => void;
const listeners = new Set<Listener>();

function systemPreference(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

function apply(t: Theme): void {
  document.documentElement.dataset['theme'] = t;
}

let current: Theme = readStored() ?? systemPreference();
apply(current);

// Track OS changes only when the user hasn't explicitly overridden.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mq = window.matchMedia('(prefers-color-scheme: light)');
  mq.addEventListener('change', () => {
    if (readStored()) return;
    current = mq.matches ? 'light' : 'dark';
    apply(current);
    for (const fn of listeners) fn(current);
  });
}

export function getTheme(): Theme {
  return current;
}

export function setTheme(t: Theme, persist = true): void {
  current = t;
  apply(t);
  if (persist) {
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* private mode */ }
  }
  for (const fn of listeners) fn(current);
}

export function toggleTheme(): Theme {
  const next: Theme = current === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

export function subscribeTheme(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
